import { Download, FileText, Layers, MoreVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";
import type { CatalogueCardEntry } from "content/catalogue";
import { TruncatedText } from "components/shared/truncated-text";
import dayjs from "dayjs";
import { Link, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { productCodeToExplorerLayerIdMappings } from "../constants";
import { getFeatureIcon, humanizeFeatures } from "./catalogue-card";
import { viewDatasetInExplorer } from "./explorer-navigation";

interface CatalogueListItemProps {
  entry: CatalogueCardEntry;
  /** From the datasets API, so absent until that query resolves. */
  updatedDate?: string;
}

export const CatalogueListItem = ({
  entry,
  updatedDate,
}: CatalogueListItemProps) => {
  const history = useHistory();
  const { resolvedTheme } = useTheme();
  const image = resolvedTheme === "dark" ? entry.imageDark : entry.imageLight;
  const hasExplorerLayer = !!productCodeToExplorerLayerIdMappings.get(
    entry.datasetProductCode,
  );
  const dataType = entry.metadata.dataType[0];

  return (
    <div
      className="flex items-center gap-4 rounded-lg border p-3"
      data-testid="catalogue-item"
    >
      <div className="size-14 shrink-0 overflow-hidden rounded-md">
        <img
          src={image}
          alt={entry.displayName}
          loading="lazy"
          className={cn(
            "size-full object-cover",
            entry.comingSoon && "scale-110 blur-sm grayscale",
          )}
        />
      </div>

      <Link
        to={`${Routes.dataCatalogue}/${entry.slug}`}
        className="group flex min-w-0 flex-1 flex-col gap-0.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex items-center gap-2">
          <TruncatedText className="font-medium group-hover:underline group-focus-visible:underline">
            {entry.displayName}
          </TruncatedText>
          {entry.comingSoon && <Badge variant="secondary">Coming soon</Badge>}
        </div>
        <p className="line-clamp-1 text-sm text-muted-foreground">
          {entry.subtitle}
        </p>
      </Link>

      <div className="hidden shrink-0 items-center gap-4 text-xs text-muted-foreground lg:flex">
        <Badge variant="outline">{entry.metadata.theme}</Badge>
        {!entry.comingSoon && (
          <span className="flex items-center gap-1 tabular-nums">
            {getFeatureIcon(dataType)}
            {Intl.NumberFormat("en", { notation: "compact" }).format(
              entry.metadata.totalFeatureCount,
            )}{" "}
            {humanizeFeatures(dataType)}
          </span>
        )}
        {updatedDate && <span>{dayjs(updatedDate).format("MMM YYYY")}</span>}
      </div>

      <div className="flex shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="outline" aria-label="Actions">
              <MoreVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`${Routes.dataCatalogue}/${entry.slug}`}>
                <FileText />
                Details
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                history.push(
                  `${Routes.dataDownload}?product=${entry.datasetProductCode}`,
                )
              }
            >
              <Download />
              Download
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!hasExplorerLayer}
              onClick={() =>
                viewDatasetInExplorer(entry.datasetProductCode, history)
              }
            >
              <Layers />
              Explore
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
